import { useRef, useCallback } from 'react';
import { getCellCoords } from '../utils/helpers';
import { defaultColumnWidth, defaultRowHeight } from '../constants/MasterSpreadsheetStyles';

export const useSpreadsheetMouse = ({
  // State dependencies
  visibleColumnNames,
  filteredData,
  columnWidths,
  rowHeights,
  isSelecting,
  isSelectingRows,
  
  // State setters
  setColumnWidths,
  setRowHeights,
  setIsSelecting,
  setIsSelectingRows,
  setActiveCell,
  setSelectionStart,
  setSelectionEnd,
  setEditingCell,
  
  // Action functions
  selectEntireRow,
  selectAllCells,
}) => {
  const resizingRef = useRef({
    isResizingCol: false,
    isResizingRow: false,
    resizingColIndex: null,
    resizingRowIndex: null,
    startX: null,
    startY: null,
    startWidth: null,
    startHeight: null,
  });

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;

    const targetTagName = e.target.tagName.toLowerCase();
    if (
      targetTagName === 'input' ||
      targetTagName === 'textarea' ||
      targetTagName === 'select' ||
      e.target.classList.contains('react-select__control') ||
      e.target.closest('.react-select__control')
    ) {
      return;
    }

    const cell = e.target.closest('td, th');
    if (!cell) return;

    const [row, col] = getCellCoords(cell);

    // Handle column resizing
    if (e.target.classList.contains('col-resize-handle')) {
      e.preventDefault();
      const columnKey = Object.keys(visibleColumnNames)[cell.cellIndex - 1];
      resizingRef.current = {
        isResizingCol: true,
        resizingColIndex: cell.cellIndex - 1,
        startX: e.clientX,
        startWidth: columnWidths[columnKey] || defaultColumnWidth,
        isResizingRow: false,
        resizingRowIndex: null,
        startY: null,
        startHeight: null,
      };
    } 
    // Handle row resizing
    else if (e.target.classList.contains('row-resize-handle')) {
      e.preventDefault();
      resizingRef.current = {
        isResizingRow: true,
        resizingRowIndex: cell.closest('tr').rowIndex - 2,
        startY: e.clientY,
        startHeight: rowHeights[cell.closest('tr').rowIndex - 2] || defaultRowHeight,
        isResizingCol: false,
        resizingColIndex: null,
        startX: null,
        startWidth: null,
      };
    } 
    // Handle cell selection
    else {
      // Only prevent default for special cells, not regular data cells
      if (cell.classList.contains('row-header') && !e.target.classList.contains('row-resize-handle')) {
        e.preventDefault();
        selectEntireRow(row, visibleColumnNames);
        setIsSelecting(true);
        setIsSelectingRows(true);
        setSelectionStart([row, 0]);
        setSelectionEnd([row, Object.keys(visibleColumnNames).length - 1]);
        setActiveCell([row, 0]);
      } else if (cell.classList.contains('select-all')) {
        e.preventDefault();
        selectAllCells(filteredData, visibleColumnNames);
      } else {
        // Don't prevent default for regular cells to allow normal click behavior
        setActiveCell([row, col]);
        setSelectionStart([row, col]);
        setSelectionEnd([row, col]);
      }

      setIsSelecting(true);
      setEditingCell(null);
    }
    
    if (!filteredData[row]) return;
  }, [
    visibleColumnNames,
    filteredData,
    columnWidths,
    rowHeights,
    selectEntireRow,
    selectAllCells,
    setIsSelecting,
    setIsSelectingRows,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
    setEditingCell,
  ]);

  const handleMouseMove = useCallback((e) => {
    const {
      isResizingCol,
      isResizingRow,
      resizingColIndex,
      resizingRowIndex,
      startX,
      startY,
      startWidth,
      startHeight,
    } = resizingRef.current;

    // Handle column resizing
    if (isResizingCol) {
      e.preventDefault();
      const startWidthNumeric = parseInt(startWidth, 10);
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(75, startWidthNumeric + deltaX);
      const columnKey = Object.keys(visibleColumnNames)[resizingColIndex];

      setColumnWidths((prev) => {
        return { ...prev, [columnKey]: newWidth };
      });
    } 
    // Handle row resizing
    else if (isResizingRow) {
      e.preventDefault();
      const deltaY = e.clientY - startY;
      const newHeight = Math.max(24, startHeight + deltaY);

      setRowHeights((prev) => ({
        ...prev,
        [resizingRowIndex]: newHeight,
      }));
    } 
    // Handle selection dragging
    else if (isSelecting) {
      e.preventDefault();
      const cell = e.target.closest('td, th');
      if (!cell) return;

      if (isSelectingRows) {
        if (cell.classList.contains('row-header')) {
          const row = cell.parentElement.rowIndex - 2;
          setSelectionEnd([
            row,
            Object.keys(visibleColumnNames).length - 1,
          ]);
          setActiveCell([row, 0]);
        }
      } else {
        const [row, col] = getCellCoords(cell);
        setSelectionEnd([row, col]);
        setActiveCell([row, col]);
      }
    }
  }, [
    visibleColumnNames,
    isSelecting,
    isSelectingRows,
    setColumnWidths,
    setRowHeights,
    setSelectionEnd,
    setActiveCell,
  ]);

  const handleMouseUp = useCallback(() => {
    resizingRef.current = {
      isResizingCol: false,
      isResizingRow: false,
      resizingColIndex: null,
      resizingRowIndex: null,
      startX: null,
      startY: null,
      startWidth: null,
      startHeight: null,
    };
    setIsSelecting(false);
    setIsSelectingRows(false);
  }, [setIsSelecting, setIsSelectingRows]);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
};